import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { jobsAPI, settingsAPI } from '../services/api';

type JobType = 'price' | 'property';

const MAX_PROPOSAL_FILES = 200;
const MAX_TOTAL_SIZE_MB = 500;
const MAX_SINGLE_FILE_SIZE_MB = 20;

interface CreatedJob {
  short_id: string;
  job_type: string;
  created_at: string;
}

const CreateJobPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [jobType, setJobType] = useState<JobType>('price');
  const [noticeFile, setNoticeFile] = useState<File | null>(null);
  const [proposalFiles, setProposalFiles] = useState<File[]>([]);
  const [ndsValue, setNdsValue] = useState<string>('');
  const [deflatorIndex, setDeflatorIndex] = useState<string>('');
  const [defaultNds, setDefaultNds] = useState<number>(20);
  const [defaultDeflator, setDefaultDeflator] = useState<number>(1.0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdJob, setCreatedJob] = useState<CreatedJob | null>(null);
  const [fileWarning, setFileWarning] = useState('');

  useEffect(() => {
    loadDefaults();
  }, []);

  const loadDefaults = async () => {
    try {
      const data = await settingsAPI.get();
      console.log('Loaded default settings for CreateJobPage:', data);
      setDefaultNds(data.default_nds ?? 20);
      setDefaultDeflator(data.default_deflator ?? 1.0);
    } catch (err) {
      console.error('Failed to load default settings:', err);
      // Keep hardcoded defaults on error
      setDefaultNds(20);
      setDefaultDeflator(1.0);
    }
  };

  const formatLocalTime = (utcString: string): string => {
    const iso = utcString.replace(' ', 'T');
    const withTz = iso.includes('+') || iso.includes('Z') ? iso : iso + 'Z';
    return new Date(withTz).toLocaleString('ru-RU');
  };

  const getJobTypeName = (type: string): string => {
    const names: Record<string, string> = {
      'analyze_price': 'Анализ цен',
      'analyze_property': 'Анализ характеристик'
    };
    return names[type] || type;
  };

  // Dynamic labels based on job type
  const getNoticeLabel = (): string => {
    return jobType === 'price' ? 'Потребность' : 'Извещение о закупке';
  };

  const getProposalsLabel = (): string => {
    return jobType === 'price' ? 'КП от поставщиков' : 'Заявки от участников';
  };

  const handleProposalFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    setFileWarning('');

    const maxSingleBytes = MAX_SINGLE_FILE_SIZE_MB * 1024 * 1024;
    const warnings: string[] = [];

    // Filter out files that are too large
    const validSizeFiles = newFiles.filter((f: File) => {
      if (f.size > maxSingleBytes) {
        warnings.push(`"${f.name}" (${formatFileSize(f.size)}) превышает ${MAX_SINGLE_FILE_SIZE_MB}МБ`);
        return false;
      }
      return true;
    });

    // Merge with existing files, skip duplicates by name+size
    const existing = new Set(proposalFiles.map((f: File) => `${f.name}_${f.size}`));
    const uniqueNew = validSizeFiles.filter((f: File) => !existing.has(`${f.name}_${f.size}`));
    const skipped = validSizeFiles.length - uniqueNew.length;

    const merged = [...proposalFiles, ...uniqueNew];

    if (merged.length > MAX_PROPOSAL_FILES) {
      warnings.push(`Максимум ${MAX_PROPOSAL_FILES} файлов. Добавлено: ${MAX_PROPOSAL_FILES - proposalFiles.length} из ${newFiles.length}`);
      setProposalFiles(merged.slice(0, MAX_PROPOSAL_FILES));
    } else {
      if (skipped > 0) {
        warnings.push(`Пропущено дубликатов: ${skipped}`);
      }
      setProposalFiles(merged);
    }

    if (warnings.length > 0) {
      setFileWarning(warnings.join('. '));
    }

    // Reset input so the same files can be re-selected if needed
    e.target.value = '';
  };

  const removeProposalFile = (index: number) => {
    setProposalFiles(prev => prev.filter((_: File, i: number) => i !== index));
    setFileWarning('');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
  };

  const validateFiles = (): string | null => {
    const maxTotalBytes = MAX_TOTAL_SIZE_MB * 1024 * 1024;
    const maxSingleBytes = MAX_SINGLE_FILE_SIZE_MB * 1024 * 1024;

    // Check notice file size
    if (noticeFile && noticeFile.size > maxSingleBytes) {
      return `Файл "${noticeFile.name}" превышает лимит ${MAX_SINGLE_FILE_SIZE_MB}МБ (${formatFileSize(noticeFile.size)})`;
    }

    // Check each proposal file size
    for (const file of proposalFiles) {
      if (file.size > maxSingleBytes) {
        return `Файл "${file.name}" превышает лимит ${MAX_SINGLE_FILE_SIZE_MB}МБ (${formatFileSize(file.size)})`;
      }
    }

    // Check total size
    const noticeSize = noticeFile ? noticeFile.size : 0;
    const proposalsSize = proposalFiles.reduce((sum, f) => sum + f.size, 0);
    const totalSize = noticeSize + proposalsSize;

    if (totalSize > maxTotalBytes) {
      return `Общий размер файлов (${formatFileSize(totalSize)}) превышает лимит ${MAX_TOTAL_SIZE_MB}МБ`;
    }

    // Check file count
    if (proposalFiles.length > MAX_PROPOSAL_FILES) {
      return `Максимум ${MAX_PROPOSAL_FILES} файлов предложений. Выбрано: ${proposalFiles.length}`;
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const formData = new FormData();

      if (!noticeFile) {
        throw new Error('Необходимо выбрать файл извещения');
      }
      if (proposalFiles.length === 0) {
        throw new Error('Необходимо выбрать хотя бы один файл предложения');
      }

      // Validate file sizes before upload
      const validationError = validateFiles();
      if (validationError) {
        throw new Error(validationError);
      }

      formData.append('notice_file', noticeFile);
      proposalFiles.forEach((file) => {
        formData.append('proposal_files', file);
      });

      let result;
      if (jobType === 'price') {
        if (ndsValue) {
          formData.append('nds_value', ndsValue);
        }
        if (deflatorIndex) {
          formData.append('deflator_index', deflatorIndex);
        }
        result = await jobsAPI.createPriceAnalysis(formData);
      } else {
        result = await jobsAPI.createPropertyAnalysis(formData);
      }

      // Show success message with job details
      setCreatedJob({
        short_id: result.short_id,
        job_type: result.job_type,
        created_at: result.created_at
      });
    } catch (err: any) {
      // Handle specific error codes
      if (err.response?.status === 413) {
        const noticeSize = noticeFile ? noticeFile.size : 0;
        const proposalsSize = proposalFiles.reduce((sum, f) => sum + f.size, 0);
        const totalSize = noticeSize + proposalsSize;
        setError(`Сервер отклонил запрос: размер слишком большой. Общий размер файлов: ${formatFileSize(totalSize)}. Попробуйте уменьшить количество или размер файлов.`);
      } else {
        setError(err.response?.data?.detail || err.message || 'Ошибка при создании задачи');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoToJobs = () => {
    navigate('/jobs');
  };

  const handleCreateAnother = () => {
    setCreatedJob(null);
    setNoticeFile(null);
    setProposalFiles([]);
    setNdsValue('');
    setDeflatorIndex('');
  };

  // Success modal
  if (createdJob) {
    return (
      <div>
        <h2 style={{ marginBottom: '20px' }}>{t('createJob.title')}</h2>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            backgroundColor: '#4caf50',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <span style={{ fontSize: '40px', color: 'white' }}>✓</span>
          </div>

          <h3 style={{ color: '#4caf50', marginBottom: '20px' }}>
            Задача отправлена на исполнение
          </h3>

          <div style={{
            backgroundColor: '#f5f5f5',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '30px',
            textAlign: 'left'
          }}>
            <div style={{ marginBottom: '10px' }}>
              <strong>Тип задачи:</strong> {getJobTypeName(createdJob.job_type)}
            </div>
            <div style={{ marginBottom: '10px' }}>
              <strong>ID задачи:</strong>{' '}
              <span style={{
                fontFamily: 'monospace',
                fontSize: '18px',
                color: '#1976d2',
                fontWeight: 'bold'
              }}>
                #{createdJob.short_id}
              </span>
            </div>
            <div>
              <strong>Время отправления:</strong> {formatLocalTime(createdJob.created_at)}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
            <button
              onClick={handleGoToJobs}
              style={{
                padding: '12px 30px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Перейти к задачам
            </button>
            <button
              onClick={handleCreateAnother}
              style={{
                padding: '12px 30px',
                backgroundColor: '#f5f5f5',
                color: '#333',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Создать ещё
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ marginBottom: '20px' }}>{t('createJob.title')}</h2>

      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ marginBottom: '30px', display: 'flex', gap: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              value="price"
              checked={jobType === 'price'}
              onChange={() => setJobType('price')}
              style={{ marginRight: '8px' }}
            />
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
              {t('createJob.priceAnalysis')}
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              value="property"
              checked={jobType === 'property'}
              onChange={() => setJobType('property')}
              style={{ marginRight: '8px' }}
            />
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
              {t('createJob.propertyAnalysis')}
            </span>
          </label>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Notice file upload */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '15px' }}>
              {getNoticeLabel()} *
            </label>
            <div style={{
              border: '2px dashed #ccc',
              borderRadius: '8px',
              padding: '20px',
              backgroundColor: noticeFile ? '#e8f5e9' : '#fafafa',
              transition: 'all 0.2s'
            }}>
              <input
                type="file"
                id="notice-file-input"
                onChange={(e) => setNoticeFile(e.target.files?.[0] || null)}
                accept=".pdf,.xlsx,.xls,.docx,.doc,.csv,.txt"
                required
                style={{ display: 'none' }}
              />
              <div style={{ textAlign: 'center' }}>
                <label
                  htmlFor="notice-file-input"
                  style={{
                    display: 'inline-block',
                    padding: '10px 24px',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'background-color 0.2s'
                  }}
                >
                  📁 Выбрать файл
                </label>
                {!noticeFile && (
                  <div style={{ marginTop: '10px', color: '#999', fontSize: '13px' }}>
                    PDF, Excel, Word или текстовый файл
                  </div>
                )}
              </div>
              {noticeFile && (
                <div style={{
                  marginTop: '12px',
                  padding: '10px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px' }}>📄</span>
                    <span style={{ fontSize: '14px', color: '#333' }}>{noticeFile.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNoticeFile(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#d32f2f',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '4px'
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Proposal files upload */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '15px' }}>
              {getProposalsLabel()} *
            </label>
            <div style={{
              border: '2px dashed #ccc',
              borderRadius: '8px',
              padding: '20px',
              backgroundColor: proposalFiles.length > 0 ? '#e3f2fd' : '#fafafa',
              transition: 'all 0.2s'
            }}>
              <input
                type="file"
                id="proposal-files-input"
                multiple
                onChange={handleProposalFilesChange}
                accept=".pdf,.xlsx,.xls,.docx,.doc,.csv,.txt"
                style={{ display: 'none' }}
              />
              <div style={{ textAlign: 'center' }}>
                <label
                  htmlFor="proposal-files-input"
                  style={{
                    display: 'inline-block',
                    padding: '10px 24px',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'background-color 0.2s'
                  }}
                >
                  📁 {proposalFiles.length > 0 ? 'Добавить ещё файлы' : 'Выбрать файлы'}
                </label>
                {proposalFiles.length === 0 && (
                  <div style={{ marginTop: '10px', color: '#999', fontSize: '13px' }}>
                    Можно выбрать несколько файлов. Добавляйте по одному или группой.
                  </div>
                )}
              </div>
              {fileWarning && (
                <div style={{
                  color: '#f57c00',
                  fontSize: '13px',
                  marginTop: '10px',
                  padding: '6px 10px',
                  backgroundColor: '#fff3e0',
                  borderRadius: '4px'
                }}>
                  ⚠️ {fileWarning}
                </div>
              )}
              {proposalFiles.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <span style={{
                      fontSize: '13px',
                      color: '#1976d2',
                      fontWeight: '500'
                    }}>
                      Файлов: {proposalFiles.length} (макс. {MAX_PROPOSAL_FILES})
                      {' · '}
                      {formatFileSize(proposalFiles.reduce((sum: number, f: File) => sum + f.size, 0))}
                    </span>
                    <button
                      type="button"
                      onClick={() => { setProposalFiles([]); setFileWarning(''); }}
                      style={{
                        padding: '4px 10px',
                        backgroundColor: '#ffebee',
                        color: '#d32f2f',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Очистить все
                    </button>
                  </div>
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    padding: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    {proposalFiles.map((file: File, index: number) => (
                      <div
                        key={`${file.name}_${file.size}_${index}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '5px 8px',
                          borderBottom: index < proposalFiles.length - 1 ? '1px solid #f0f0f0' : 'none',
                          borderRadius: '4px'
                        }}
                      >
                        <span style={{ fontSize: '14px' }}>📄</span>
                        <span style={{ fontSize: '13px', color: '#333', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {file.name}
                        </span>
                        <span style={{ fontSize: '11px', color: '#999', whiteSpace: 'nowrap' }}>
                          {formatFileSize(file.size)}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeProposalFile(index)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#999',
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            lineHeight: 1
                          }}
                          onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.color = '#d32f2f')}
                          onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.color = '#999')}
                          title="Удалить файл"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {jobType === 'price' && (
            <>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  {t('createJob.ndsLabel')} ({t('createJob.ndsDefault')}: {defaultNds}%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={ndsValue}
                  onChange={(e) => setNdsValue(e.target.value)}
                  placeholder={defaultNds.toString()}
                  style={{
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    width: '100%'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  {t('createJob.deflatorIndex')} (по умолчанию: {defaultDeflator})
                </label>
                <input
                  type="number"
                  step="0.001"
                  value={deflatorIndex}
                  onChange={(e) => setDeflatorIndex(e.target.value)}
                  placeholder={defaultDeflator.toString()}
                  style={{
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    width: '100%'
                  }}
                />
              </div>
            </>
          )}

          {/* Total size indicator */}
          {(noticeFile || proposalFiles.length > 0) && (
            <div style={{
              backgroundColor: '#f5f5f5',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '20px',
              fontSize: '14px',
              color: '#666'
            }}>
              <strong>Общий размер:</strong>{' '}
              {formatFileSize((noticeFile?.size || 0) + proposalFiles.reduce((sum: number, f: File) => sum + f.size, 0))}
              {' / '}{MAX_TOTAL_SIZE_MB}МБ
              {' · '}
              <strong>Файлов:</strong> {(noticeFile ? 1 : 0) + proposalFiles.length}
            </div>
          )}

          {error && (
            <div style={{
              color: '#d32f2f',
              backgroundColor: '#ffebee',
              padding: '10px',
              borderRadius: '4px',
              marginBottom: '20px'
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? t('createJob.creating') : t('createJob.createButton')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateJobPage;
